<script setup>
import {computed, onMounted, ref} from "vue";
import {createConfirmDialog} from "vuejs-confirm-dialog";
import ServiceRequirementModal from "@/components/modals/ServiceRequirementModal.vue";
import {formatDate} from "@/helper";
import router from "@/router";
import {useDataTable} from "@/utils/datatableHelper";
import ServiceRequirementService from "@/services/ServiceRequirementService";
import {useI18n} from 'vue-i18n'

const props = defineProps({
    /** null = zobrazí vše, false = pouze newyřešené, true = pouze vyřešené (archiv) */
    resolved: {
        type: Boolean,
        default: null,
    },
    storageKey: {
        type: String,
        default: 'SERVICE-REQUIREMENTS',
    },
});

const {t} = useI18n();

const defaultFilter = props.resolved === null ? {} : { resolved: props.resolved };
const datatable = useDataTable(fetchRequirements, 'created_at', 20, false, defaultFilter, props.storageKey);

/** Sloupce dle zadání: Klient, Datum vytvoření, [Datum vyřešení pro archiv], Řešitel, Akce. */
const columns = computed(() => {
    const base = [
        {
            label: t('serviceRequirementsListComponent.client'),
            field: 'client_author',
            filterOptions: { enabled: true, skipDiacritics: true, placeholder: ' ' },
        },
        /* Typ požadavku je dočasně skryt — interně se defaultuje na "message". */
        {
            label: t('serviceRequirementsListComponent.created_at'),
            field: 'created_at',
            sortable: true,
            filterOptions: { enabled: true, datePicker: true },
        },
    ];

    if (props.resolved === true) {
        base.push({
            label: t('serviceRequirementsListComponent.resolved_at'),
            field: 'resolved_at',
            sortable: true,
            filterOptions: { enabled: true, datePicker: true },
        });
    }

    base.push(
        {
            label: t('serviceRequirementsListComponent.resolver'),
            field: 'resolved_by',
            sortable: false,
            filterOptions: { enabled: true, placeholder: ' ' },
        },
        { label: t('serviceRequirementsListComponent.action'), field: 'action', sortable: false, html: true },
    );

    return base;
});

onMounted(() => {
  //fetchClients();
});

function openServiceRequirementModal(serviceRequirement) {
    const {reveal, onConfirm, onCancel} = createConfirmDialog(ServiceRequirementModal, {serviceRequirement: serviceRequirement});
    reveal();
}

function fetchRequirements(body) {
  datatable.isLoading.value = true;
  ServiceRequirementService.fetchRecords(body).then(response => {
      datatable.totalRecords.value = response.meta.total;
      datatable.rows.value = response.data;
      datatable.isLoading.value = false;
    // datatable.totalRecords.value = response.meta.total;
    // requirements.value = response.data;
    // isLoading.value = false;
  });
}

function navigateTo(to) {
  router.push(to);
}

/** Přiřadí přihlášeného uživatele jako řešitele daného požadavku a aktualizuje řádek v tabulce. */
function assignSelf(row) {
    ServiceRequirementService.assignSelf(row.id).then(updated => {
        if (updated) {
            // Aktualizujeme pouze resolved_by na daném řádku — neprovádíme full reload
            const idx = datatable.rows.value.findIndex(r => r.id === row.id);
            if (idx !== -1) datatable.rows.value[idx].resolved_by = updated.resolved_by;
        }
    });
}

/** Umožňuje rodiči přenačíst tabulku po vytvoření nového požadavku. */
defineExpose({ reload: () => datatable.fetchWithValues() });

/** Odebere řešitele z požadavku a aktualizuje řádek v tabulce. */
function unassignResolver(row) {
    ServiceRequirementService.unassignSelf(row.id).then(updated => {
        if (updated) {
            const idx = datatable.rows.value.findIndex(r => r.id === row.id);
            if (idx !== -1) datatable.rows.value[idx].resolved_by = null;
        }
    });
}

</script>

<template>
    <div class="mt-3">
      <vue-good-table
          :columns="columns"
          :rows="datatable.rows.value"
          mode="remote"
          @page-change="datatable.onPageChange"
          @sort-change="datatable.onSortChange"
          @column-filter="datatable.onColumnFilter"
          @per-page-change="datatable.onPerPageChange"
          :totalRows="datatable.totalRecords.value"
          :sort-options="{
          enabled: true,
          initialSortBy: {field: 'created_at', type: 'desc'}
        }"
          :pagination-options="{
            enabled: true,
            perPage: datatable.pageLength.value,
            rowsPerPageLabel: t('serviceRequirementsListComponent.entriesPerPage'),
            nextLabel: t('serviceRequirementsListComponent.next'),
            prevLabel: t('serviceRequirementsListComponent.previous'),
            ofLabel: 'z',
          }">
        <!--
          Vlastní filter slot — date columns dostanou datepicker,
          ostatní filtrovatelné sloupce dostanou text input s křížkem pro vymazání.
          :value je nutné pro obnovu hodnoty filtru po návratu ze stránky (načte se z localStorage).
        -->
        <template #column-filter="{ column, updateFilters }">
            <!-- Datový filtr — nativní prohlížečový datepicker, hodnota se obnoví z uloženého filtru -->
            <input
                v-if="column.filterOptions?.datePicker"
                type="date"
                class="vgt-input"
                :value="datatable.filter.value[column.field] ?? ''"
                @change="updateFilters(column, $event.target.value)"
            />
            <!-- Textový filtr — křížek pro vymazání se zobrazí jen pokud je zadána hodnota -->
            <div v-else-if="column.filterOptions?.enabled" class="filter-with-clear">
                <input
                    type="text"
                    class="vgt-input"
                    :placeholder="column.filterOptions?.placeholder || ''"
                    :value="datatable.filter.value[column.field] ?? ''"
                    @input="updateFilters(column, $event.target.value)"
                />
                <button
                    v-if="datatable.filter.value[column.field]"
                    type="button"
                    class="filter-clear-btn"
                    tabindex="-1"
                    @click="updateFilters(column, '')"
                >
                    &times;
                </button>
            </div>
        </template>

        <template #table-row="props">
            <span v-if="props.column.field === 'action'" style="float: right">
                <router-link :to="{ name: 'BackOfficeServiceRequirementDetail', params: { id: props.row.id } }">
                    <button type="button" class="btn btn-link"><i class="mdi mdi-eye"/></button>
                </router-link>
            </span>
            <span v-else-if="props.column.field === 'client_author'">
                {{ props.row.client?.label }}<br/>
                <small class="text-muted">{{ props.row.user?.label }}</small>
            </span>
            <span v-else-if="props.column.field === 'created_at'">
                {{ formatDate(props.row.created_at, 'd.m.Y H:i') }}
            </span>
            <span v-else-if="props.column.field === 'resolved_at'">
                {{ props.row.resolved_at ? formatDate(props.row.resolved_at, 'd.m.Y H:i') : '---' }}
            </span>
            <!--
              Řešitel: zobrazí jméno pokud je resolved_by vyplněn,
              jinak tlačítko "Přiřadit mně" pro přiřazení přihlášeného BO uživatele.
            -->
            <span v-else-if="props.column.field === 'resolved_by'">
                <!-- Řešitel přiřazen: zobrazí jméno + tlačítko pro odebrání (jako u dokumentů) -->
                <span v-if="props.row.resolved_by">
                    {{ props.row.resolved_by.label }}
                    <button type="button" class="btn btn-link p-0 ms-1" :title="t('serviceRequirementsListComponent.unassign')" @click="unassignResolver(props.row)">
                        <i class="mdi mdi-account-minus"></i>
                    </button>
                </span>
                <button
                    v-else
                    type="button"
                    class="btn btn-secondary btn-sm"
                    @click="assignSelf(props.row)"
                >
                    <i class="mdi mdi-account-plus"></i> {{ t('serviceRequirementsListComponent.assign_self') }}
                </button>
            </span>
            <span v-else>{{ props.formattedRow[props.column.field] }}</span>
        </template>
        <template v-slot:emptystate>
          <div class="text-center">
            {{ datatable.isLoading.value ? t('serviceRequirementsListComponent.data_loading') : t('serviceRequirementsListComponent.no_records_found') }}
          </div>
        </template>
      </vue-good-table>
    </div>
</template>

<style scoped>
.filter-with-clear {
  position: relative;
}

/* Extra padding vpravo, aby text nepřekrýval křížek */
.filter-with-clear .vgt-input {
  padding-right: 22px;
}

.filter-clear-btn {
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #aaa;
  font-size: 15px;
  padding: 0;
  line-height: 1;
}

.filter-clear-btn:hover {
  color: #555;
}
</style>
